import React, { useEffect, useState } from 'react';
import { SET_TEMPO_STATUS, getMidiBpm, setMidiBpm } from '../../audio/midiTempo';

import { MIDI } from 'spessasynth_core';
import { border } from 'polished';
import { controlChangeMessages } from './midiMessageNumers';
import { drumKits } from './drumKits';
import { drumNotes } from './drumNotes';
import { instruments } from './instruments';

// ------------------------------------------------------
// Helpers
// ------------------------------------------------------
const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B'
];

function midiNoteToName(note) {
  const name = NOTE_NAMES[note % 12];
  const octave = Math.floor(note / 12) - 1;
  return `${name}${octave}`;
}

// spessasynth_core lagrar meta-events med själva meta-typbyten (t.ex. 0x51 för Set
// Tempo, 0x03 för Track Name) som messageStatusByte - inte 0xFF. De ligger alltså
// alltid under 0x80 och måste kollas separat innan man bitmaskar ut kanal-typen.
const META_TYPE_NAMES = {
  0x00: 'Sequence Number',
  0x01: 'Text',
  0x02: 'Copyright',
  0x03: 'Track Name',
  0x04: 'Instrument Name',
  0x05: 'Lyric',
  0x06: 'Marker',
  0x07: 'Cue Point',
  0x08: 'Program Name',
  0x20: 'MIDI Channel Prefix',
  0x21: 'MIDI Port',
  0x2F: 'End of Track',
  [SET_TEMPO_STATUS]: 'Set Tempo',
  0x54: 'SMPTE Offset',
  0x58: 'Time Signature',
  0x59: 'Key Signature',
  0x7F: 'Sequencer Specific'
};

function getMessageType(statusByte) {
  if (statusByte < 0x80) {
    return META_TYPE_NAMES[statusByte] || 'Unknown Meta';
  }
  const type = statusByte & 0xF0;
  switch (type) {
    case 0x80: return 'Note Off';
    case 0x90: return 'Note On';
    case 0xA0: return 'Polyphonic Key Pressure';
    case 0xB0: return 'Control Change';
    case 0xC0: return 'Program Change';
    case 0xD0: return 'Channel Pressure';
    case 0xE0: return 'Pitch Bend';
    case 0xF0: return 'System / Meta';
    default: return 'Unknown';
  }
}

function decodeMetaText(data) {
  try {
    return new TextDecoder('utf-8').decode(data);
  } catch (e) {
    return Array.from(data).map(b => String.fromCharCode(b)).join('');
  }
}

function formatEvent(event) {
  const statusByte = event.messageStatusByte;
  const type = getMessageType(statusByte);
  const isMeta = statusByte < 0x80;
  const channel = (isMeta || statusByte >= 0xF0) ? '-' : (statusByte & 0x0F) + 1;
  const data = event.messageData;

  switch (type) {
    case 'Note On':
    case 'Note Off': {
      const note = data[0];
      const velocity = data[1];
      if (channel === 10) {
        return { type, channel, value: `${drumNotes[note]} - velocity: ${velocity}` };
      }
      return { type, channel, value: `${midiNoteToName(note)} - velocity: ${velocity}` };
    }

    case 'Control Change':
      return { type, channel, value: `${controlChangeMessages[data[0]]}: ${data[1]}` };

    case 'Program Change':
      if (channel === 10) {
        // Drum kits-listan är 1-baserad, programnumret i data[0] är 0-baserat
        return { type, channel, value: drumKits[String(data[0] + 1)] };
      } else {
        return { type, channel, value: instruments[String(data[0])] };
      }

    case 'Channel Pressure':
      return { type, channel, value: `pressure: ${data[1]}` };

    case 'Pitch Bend': {
      const lsb = data[1];
      const msb = data[2];
      const bend = ((msb << 7) | lsb) - 8192;
      return { type, channel, value: `pitch bend: ${bend}` };
    }

    case 'System / Meta':
      return {
        type,
        channel,
        value: `data: ${Array.from(data).slice(1).join(', ')}`
      };

    case 'Set Tempo': {
      const usPerQuarter = (data[0] << 16) | (data[1] << 8) | data[2];
      return { type, channel, value: `${Math.round(60000000 / usPerQuarter)} BPM` };
    }

    case 'Time Signature':
      return { type, channel, value: `${data[0]}/${2 ** data[1]}` };

    case 'Key Signature': {
      const sharpsFlats = data[0] > 127 ? data[0] - 256 : data[0]; // signed byte
      const mode = data[1] === 1 ? 'minor' : 'major';
      const accidentals = sharpsFlats === 0
        ? 'no sharps/flats'
        : `${Math.abs(sharpsFlats)} ${sharpsFlats > 0 ? 'sharp(s)' : 'flat(s)'}`;
      return { type, channel, value: `${accidentals}, ${mode}` };
    }

    case 'Track Name':
    case 'Instrument Name':
    case 'Text':
    case 'Copyright':
    case 'Lyric':
    case 'Marker':
    case 'Cue Point':
    case 'Program Name':
      return { type, channel, value: decodeMetaText(data) };

    case 'MIDI Channel Prefix':
      return { type, channel, value: `channel: ${data[0] + 1}` };

    case 'MIDI Port':
      return { type, channel, value: `port: ${data[0]}` };

    case 'End of Track':
      return { type, channel, value: '' };

    default:
      return { type, channel, value: '' };
  }
}

// Gemensam källa för hur spårnamn tolkas till en föreslagen kanal och ett
// föreslaget instrument (visas även i info-panelen för spårnamn i UI:t).
// Kontrolleras i tur och ordning - första nyckelordet som matchar vinner.
const NAME_HINT_RULES = [
  { keywords: ['drum'], channel: 10, instrument: 'Standard Drum Kit' },
  { keywords: ['bass'], channel: 2, instrument: 'Acoustic Bass' },
  { keywords: ['harmony', 'melody'], channel: 3, instrument: 'Acoustic Grand Piano' },
  { keywords: ['piano'], channel: 1, instrument: 'Electric Piano 1' }
];

function matchNameHintRule(name = '') {
  const n = name.toLowerCase();
  return NAME_HINT_RULES.find(rule => rule.keywords.some(kw => n.includes(kw)));
}

function guessChannelFromName(name = '') {
  return matchNameHintRule(name)?.channel ?? '-';
}

function guessInstrumentFromName(name = '') {
  return matchNameHintRule(name)?.instrument ?? '-';
}

function cloneMIDI(midi) {
  // Skapar ett nytt MIDI-objekt baserat på nuvarande innehåll
  return new MIDI(midi.writeMIDI());
}

const TRACK_NAME_STATUS = 0x03;

// Sätter/uppdaterar spårets Track Name-event i den faktiska spårdatan.
// midi.trackNames är bara en cache som byggs om från spårdatan varje gång
// midi klonas (cloneMIDI/flush), så själva eventet måste uppdateras - annars
// försvinner namnändringen så fort en annan redigering görs.
function applyTrackNameToTrack(newMIDI, trackIndex, name) {
  const track = newMIDI.tracks[trackIndex];
  const nameData = new TextEncoder().encode(name);

  const existing = track.find(ev => ev.messageStatusByte === TRACK_NAME_STATUS);
  if (existing) {
    existing.messageData = nameData;
  } else {
    track.unshift({
      ticks: track[0]?.ticks ?? 0,
      messageStatusByte: TRACK_NAME_STATUS,
      messageData: nameData
    });
  }
}

// ------------------------------------------------------
// Component
// ------------------------------------------------------
export default function MIDIEditor({ midiUrl, parsedMIDI, onParsedMIDIChange, onSave }) {
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [trackChannels, setTrackChannels] = useState([]);
  const [trackNames, setTrackNames] = useState([]);
  const [editingEventIndex, setEditingEventIndex] = useState(null);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [editingChannel, setEditingChannel] = useState(1);
  const [transposeOctaves, setTransposeOctaves] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [bpmInput, setBpmInput] = useState(120);

  // Ny state: instrument per spår (visas i track-tabellens select)
  const [trackInstruments, setTrackInstruments] = useState([]);
  // Namnfältets (ej ännu applicerade) innehåll per spår
  const [trackNameInputs, setTrackNameInputs] = useState([]);
  const [showNameHints, setShowNameHints] = useState(false);

  // Initiera / uppdatera track-kanaler, instrument och selektion när parsedMIDI ändras
  useEffect(() => {
    if (!parsedMIDI?.tracks?.length) {
      setTrackChannels([]);
      setTrackInstruments([]);
      setSelectedTrackIndex(0);
      setSelectedRow(null);
      setTrackNames([]);
      setTrackNameInputs([]);
      setBpmInput(120);
      return;
    }
    const tracks = parsedMIDI.tracks;
    const names = parsedMIDI.trackNames || [];
    setTrackNames(names);
    setTrackNameInputs(tracks.map((_, idx) => names[idx] || `Track ${idx + 1}`));
    setBpmInput(getMidiBpm(parsedMIDI));

    setTrackChannels(
      tracks.map((_, idx) => guessChannelFromName(names[idx]))
    );

    // Förifyll instrument baserat på namn (se NAME_HINT_RULES)
    setTrackInstruments(
      tracks.map((_, idx) => guessInstrumentFromName(names[idx]))
    );

    if (selectedTrackIndex >= tracks.length) {
      setSelectedTrackIndex(0);
    }
    setSelectedRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedMIDI]);

    // När en ny fil laddas → nollställ dirty
    useEffect(() => {
      setIsDirty(false);
    }, [midiUrl]);
    
  if (!parsedMIDI?.tracks?.length) {
    return <p>No MIDI data loaded.</p>;
  }

  const tracks = parsedMIDI.tracks;
  const selectedTrack = tracks[selectedTrackIndex];

  // ------------------------------------------------------
  // Channel apply-funktioner (immutabla)
  // ------------------------------------------------------
  function applyChannelToTrack(trackIndex, channel) {
    if (channel === '-' || !parsedMIDI) return;

    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];
    const track = newMIDI.tracks[trackIndex];

    track.forEach(event => {
      const status = event.messageStatusByte;
      // Endast riktiga kanalmeddelanden (Note On/Off, CC, Program Change m.fl.)
      // bär en kanal i statusbyten - meta-events (status < 0x80, t.ex. Track
      // Name/Set Tempo) ska lämnas orörda, annars förvanskas de.
      if (status >= 0x80 && status < 0xF0) {
        const type = status & 0xF0;
        event.messageStatusByte = type | (channel - 1);
      }
    });

    changeParsedMIDI(newMIDI);
  }

  function applyChannelToAllTracks() {
    if (!parsedMIDI) return;

    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];

    newMIDI.tracks.forEach((track, idx) => {
      const ch = trackChannels[idx];
      if (ch === '-' || ch == null) return;

      track.forEach(event => {
        const status = event.messageStatusByte;
        if (status >= 0x80 && status < 0xF0) {
          const type = status & 0xF0;
          event.messageStatusByte = type | (ch - 1);
        }
      });
    });

    changeParsedMIDI(newMIDI);
  }

  // ------------------------------------------------------
  // Instrument-funktioner (per spår)
  // ------------------------------------------------------
  // ------------------------------------------------------
  // Hjälpfunktioner
  // ------------------------------------------------------

  // Returnerar { programNumber, isDrums } eller null om inget instrument finns
  function getProgramNumber(trackIndex) {
    const instrumentName = trackInstruments[trackIndex];
    if (!instrumentName || instrumentName === '-') return null;

    const trackName = trackNames[trackIndex]?.toLowerCase() || '';
    const isDrums = trackName.includes('drum');
    const source = isDrums ? drumKits : instruments;

    let programNumberRaw = null;
    for (const [num, label] of Object.entries(source)) {
      if (label === instrumentName) {
        programNumberRaw = Number(num);
        break;
      }
    }
    if (programNumberRaw == null) return null;

    const programNumber = isDrums ? programNumberRaw - 1 : programNumberRaw;
    return { programNumber, isDrums };
  }

  // Applicerar Program Change på ett specifikt spår i ett givet MIDI-objekt.
  // Tar bort alla ev. befintliga Program Change-event på spåret (oavsett var
  // de ligger) innan det nya läggs in längst fram - annars kan ett senare
  // event (t.ex. efter inledande meta-events) skriva över det igen.
  function applyProgramChangeToTrack(newMIDI, trackIndex, programNumber) {
    const track = newMIDI.tracks[trackIndex];
    const firstTick = track[0]?.ticks ?? 0;

    const existingProgramChanges = track.filter(ev => (ev.messageStatusByte & 0xF0) === 0xC0);
    const existingChannel = existingProgramChanges.length > 0
      ? existingProgramChanges[0].messageStatusByte & 0x0F
      : undefined;

    const channel = existingChannel !== undefined
      ? existingChannel
      : (trackChannels[trackIndex] === '-' || trackChannels[trackIndex] == null
        ? 0
        : trackChannels[trackIndex] - 1);

    for (let i = track.length - 1; i >= 0; i--) {
      if ((track[i].messageStatusByte & 0xF0) === 0xC0) {
        track.splice(i, 1);
      }
    }

    track.unshift({
      ticks: firstTick,
      messageStatusByte: 0xC0 | channel,
      messageData: new Uint8Array([programNumber])
    });
  }

  // Klonar parsedMIDI och kopierar trackNames
  function cloneWithTrackNames() {
    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];
    return newMIDI;
  }

  // ------------------------------------------------------
  // Refaktorerade huvudmetoder
  // ------------------------------------------------------

  function applyInstrumentToTrack(trackIndex) {
    if (!parsedMIDI) return;

    const info = getProgramNumber(trackIndex);
    if (!info) return;

    const { programNumber } = info;

    const newMIDI = cloneWithTrackNames();
    applyProgramChangeToTrack(newMIDI, trackIndex, programNumber);

    changeParsedMIDI(newMIDI);
  }

  function applyTrackName(trackIndex) {
    if (!parsedMIDI) return;

    const newName = (trackNameInputs[trackIndex] || '').trim();
    if (!newName) return;

    const names = [...trackNames];
    names[trackIndex] = newName;

    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = names;
    applyTrackNameToTrack(newMIDI, trackIndex, newName);

    setTrackNames(names);
    setTrackNameInputs(prev => {
      const updated = [...prev];
      updated[trackIndex] = newName;
      return updated;
    });
    changeParsedMIDI(newMIDI);
  }

  function autoSetInstruments() {
    if (!parsedMIDI) return;

    const newMIDI = cloneWithTrackNames();

    trackInstruments.forEach((inst, idx) => {
      const info = getProgramNumber(idx);
      if (!info) return;

      applyProgramChangeToTrack(newMIDI, idx, info.programNumber);
    });

    changeParsedMIDI(newMIDI);
  }

  // ------------------------------------------------------
  // Event-funktioner
  // ------------------------------------------------------
  function changeParsedMIDI(newMIDI) {
    console.log("Applying MIDI changes...");
    setIsDirty(true);
    onParsedMIDIChange(newMIDI);
  }

  const handleSave = async () => {
    await onSave();
    setIsDirty(false);
  };

  function applyProgramChange(eventIndex, newInstrument, newChannel) {
    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];

    const track = newMIDI.tracks[selectedTrackIndex];
    const ev = track[eventIndex];

    // Program Change: statusbyte = 0xC0 | channel
    const channel = ev.messageStatusByte & 0x0F;
    console.log(`channel: ${channel}, new: ${newChannel}`)

    ev.messageStatusByte = 0xC0 | newChannel;
    ev.messageData[0] = Number(newInstrument);

    setEditingEventIndex(null);
    changeParsedMIDI(newMIDI);
  }

  function addProgramChangeEvent() {
    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];

    const track = newMIDI.tracks[selectedTrackIndex];

    const channel = trackChannels[selectedTrackIndex] === "-"
      ? 0
      : trackChannels[selectedTrackIndex] - 1;

    const newEvent = {
      ticks: 0,
      messageStatusByte: 0xC0 | channel,
      messageData: new Uint8Array([0]), // default instrument 0
    };

    track.unshift(newEvent);

    changeParsedMIDI(newMIDI);

    // Markera den nya raden
    /*     setSelectedRow(0);
        setEditingEventIndex(0);
        setEditingInstrument("0");
        setEditingChannel(1); */
  }

  function shiftOctave() {
    const noOfOctaves = Number(transposeOctaves);
    if(noOfOctaves === 0) return;
    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];
    const track = newMIDI.tracks[selectedTrackIndex];
    // Transpose single channel 
   const transposeSemitones = (noOfOctaves * 12);
      track.forEach(event => {
        const status = event.messageStatusByte;
    
        // MIDI status byte format: 0x9c = noteOn on channel c
        const eventType = status & 0xF0;  // upper nibble: type
        const channel = status & 0x0F;    // lower nibble: channel (0-15)
    
        if ((eventType === 0x90 || eventType === 0x80)) {
          // Only modify note number if present
          const note = event.messageData[0];
          const transposedNote = Math.max(0, Math.min(127, note + transposeSemitones));
          event.messageData[0] = transposedNote;
        }
      });
    changeParsedMIDI(newMIDI);
    setTransposeOctaves(0);
  }

  function applyBpm() {
    const newBpm = Number(bpmInput);
    if (!newBpm || newBpm <= 0 || newBpm > 300) return;

    const newMIDI = cloneWithTrackNames();
    setMidiBpm(newMIDI, newBpm);
    changeParsedMIDI(newMIDI);
  }

  function deleteEvent(eventIndex) {
    const newMIDI = cloneMIDI(parsedMIDI);
    newMIDI.trackNames = [...trackNames];

    const track = newMIDI.tracks[selectedTrackIndex];

    // Ta bort eventet
    track.splice(eventIndex, 1);

    // Om vi raderade den markerade raden → nollställ selection
    if (selectedRow === eventIndex) {
      setSelectedRow(null);
      setEditingEventIndex(null);
    }

    // Om vi raderade en rad ovanför den markerade → justera index
    if (selectedRow > eventIndex) {
      setSelectedRow(selectedRow - 1);
    }

    changeParsedMIDI(newMIDI);
  }

  // ------------------------------------------------------
  // Render
  // ------------------------------------------------------
  return (
    <div style={styles.container}>
      <h2>MIDI Inspector
      <button
          style={{ marginLeft: '1rem' }}
          onClick={handleSave}
          disabled={!isDirty}
        >
          Save changes
        </button></h2>
      {/* Track table */}
      <h3 style={{ marginTop: '1rem' }}>
        Tracks
        <button
          style={{ marginLeft: '0.6rem' }}
          onClick={() => setShowNameHints(prev => !prev)}
          title="Visa hur spårnamn tolkas till föreslagen kanal och instrument"
        >
          ℹ Info
        </button>
      </h3>
      {showNameHints && (
        <div style={styles.hintBox}>
          <p style={{ marginTop: 0 }}>
            Kanal och instrument föreslås automatiskt utifrån spårnamnet (första
            nyckelordet nedan som matchar, oavsett versaler/gemener, vinner).
            Dessa är bara förslag - något annat kan väljas och klicka "Set".
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Om spårnamnet innehåller</th>
                <th style={styles.th}>Föreslagen kanal</th>
                <th style={styles.th}>Föreslaget instrument</th>
              </tr>
            </thead>
            <tbody>
              {NAME_HINT_RULES.map(rule => (
                <tr key={rule.keywords.join('/')}>
                  <td style={styles.td}>"{rule.keywords.join('" eller "')}"</td>
                  <td style={styles.td}>{rule.channel}</td>
                  <td style={styles.td}>{rule.instrument}</td>
                </tr>
              ))}
              <tr>
                <td style={styles.td}>(inget av ovanstående)</td>
                <td style={styles.td}>-</td>
                <td style={styles.td}>-</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Events</th>
            <th style={styles.th}>Suggested Channel</th>
            <th style={styles.th}>Prefered Instrument</th>
          </tr>
        </thead>
        <tbody>
          {tracks.map((track, idx) => {
            const isSelected = idx === selectedTrackIndex;
            const name = trackNames[idx] || `Track ${idx + 1}`;
            const isDrums = name.toLowerCase().includes('drum');

            return (
              <tr
                key={idx}
                style={isSelected ? styles.rowSelected : styles.row}
                onClick={() => {
                  setEditingEventIndex(null);
                  setSelectedTrackIndex(idx);
                }}
              >
                <td style={styles.td}>{idx + 1}</td>
                <td style={styles.td}>
                  <input
                    type="text"
                    style={{ width: '9rem' }}
                    value={trackNameInputs[idx] ?? name}
                    onChange={e => {
                      const updated = [...trackNameInputs];
                      updated[idx] = e.target.value;
                      setTrackNameInputs(updated);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') applyTrackName(idx);
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      applyTrackName(idx);
                      setSelectedTrackIndex(idx);
                    }}
                  >
                    Set
                  </button>
                </td>
                <td style={styles.td}>{track.length}</td>
                <td style={styles.td}>
                  <select
                    value={trackChannels[idx] ?? '-'}
                    onChange={e => {
                      const raw = e.target.value;
                      const val = raw === '-' ? '-' : Number(raw);
                      const updated = [...trackChannels];
                      updated[idx] = val;
                      setTrackChannels(updated);
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <option value="-">-</option>
                    {Array.from({ length: 16 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      applyChannelToTrack(idx, trackChannels[idx]);
                      setSelectedTrackIndex(idx);
                    }}
                  >
                    Set
                  </button>
                </td>
                <td style={styles.td}>
                  <select
                    value={trackInstruments[idx] || '-'}
                    onChange={e => {
                      const updated = [...trackInstruments];
                      updated[idx] = e.target.value;
                      setTrackInstruments(updated);
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <option value="-">-</option>
                    {Object.entries(isDrums ? drumKits : instruments).map(([num, label]) => (
                      <option key={num} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      applyInstrumentToTrack(idx);
                      setSelectedTrackIndex(idx);
                    }}
                  >
                    Set
                  </button>
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={3}></td>
            <td>
              <button
                style={{ marginLeft: '0.4rem' }}
                onClick={applyChannelToAllTracks}
              >
              Set channels to all tracks
            </button>
            </td>
            <td>
              <button
                style={{ marginLeft: '0.4rem' }}
                onClick={autoSetInstruments}
              >
                Set instruments to all tracks
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      {/* Tempo */}
      <div style={{ marginTop: '1rem' }}>
        <span>Current BPM: {getMidiBpm(parsedMIDI)}</span>
        <input
          type="number"
          min="1"
          max="300"
          style={{ marginLeft: '1rem', width: '4rem' }}
          value={bpmInput}
          onChange={e => setBpmInput(e.target.value)}
          onClick={e => e.stopPropagation()}
        />
        <button onClick={applyBpm}>
          Set
        </button>
      </div>

      {/* Event table */}
      <h3 style={{ marginTop: '1rem' }}>
        Events for: {trackNames[selectedTrackIndex] || `Track ${selectedTrackIndex + 1}`}
      </h3>
      <div style={{ marginTop: '1rem' }}>
        {/* <button onClick={addProgramChangeEvent}>
          Add Program Change
        </button> */}
        <span>Shift octave</span>
        <select style={{ marginLeft: '1rem' }}
          value={transposeOctaves}
          onChange={e => {
            setTransposeOctaves(e.target.value);
          }}
          onClick={e => e.stopPropagation()}
        >
          <option value="3">+3</option>
          <option value="2">+2</option>
          <option value="1">+1</option>
          <option value="0">0</option>
          <option value="-1">-1</option>
          <option value="-2">-2</option>
          <option value="-3">-3</option>
        </select>
        <button
          onClick={shiftOctave}
        >
          Set
        </button>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Ticks</th>
            <th style={styles.th}>Event Type</th>
            <th style={styles.th}>Channel</th>
            <th style={styles.th}>Value</th>
          </tr>
        </thead>
        <tbody>
          {selectedTrack?.map((event, idx) => {
            const { type, channel, value } = formatEvent(event);
            const isSelected = idx === selectedRow;
            const rowStyle = isSelected
              ? styles.rowSelected
              : type === 'Program Change'
                ? styles.rowProgramChange
                : styles.row;

            return (
              <tr
                key={idx}
                style={rowStyle}
                onClick={() => {
                  setSelectedRow(idx);

                  if (type === "Program Change") {
                    setEditingEventIndex(idx);
                    setEditingInstrument(String(event.messageData[0])); // nuvarande instrumentnummer
                    setEditingChannel(channel);
                  } else {
                    setEditingEventIndex(null);
                  }
                }}
              >
                <td style={styles.td}>{idx + 1}</td>
                <td style={styles.td}>{event.ticks}</td>
                <td style={styles.td}>{type}</td>
                <td style={styles.td}>
                  {type === "Program Change" && idx === editingEventIndex ? (
                    <>
                      <select
                        value={editingChannel}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setEditingChannel(e.target.value);
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((num) => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    channel
                  )}
                </td>
                <td style={styles.td}>
                  {type === "Program Change" && idx === editingEventIndex ? (
                    <>
                      <select
                        value={editingInstrument}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setEditingInstrument(e.target.value);
                        }}
                      >
                        {channel === 10 ?
                          Object.entries(drumKits).map(([num, name]) => (
                            <option key={num} value={num - 1}>{name}</option>
                          ))
                          :
                          Object.entries(instruments).map(([num, name]) => (
                            <option key={num} value={num}>{name}</option>
                          ))}
                      </select>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          applyProgramChange(idx, editingInstrument, editingChannel - 1);
                        }}
                      >
                        OK
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteEvent(idx);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    value
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------
// Styles
// ------------------------------------------------------
const styles = {
  container: { border: '1px solid #ccc', borderRadius: 4, padding: '1rem', fontFamily: 'sans-serif', fontSize: '0.9rem' },
  table: { marginTop: '0.25rem', width: '100%', borderCollapse: 'collapse' },

  th: {
    borderBottom: '2px solid #444',
    padding: '0.25rem 0.35rem',
    textAlign: 'left',
    fontSize: '0.85rem',
    lineHeight: 1.1
  },

  td: {
    borderBottom: '1px solid #ccc',
    padding: '0.2rem 0.35rem',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    lineHeight: 1.1
  },

  row: { cursor: 'pointer' },
  rowSelected: { background: '#def', cursor: 'pointer' },
  rowProgramChange: { background: '#fffde7', cursor: 'pointer' },

  hintBox: {
    marginTop: '0.5rem',
    padding: '0.6rem 0.8rem',
    background: '#f7f7f7',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.85rem'
  }
};